import { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'urql';
import PageHeader from '../../../components/PageHeader.jsx';
import StickyActions from '../../../components/StickyActions.jsx';
import DataTable from '../../../components/Table/DataTable.jsx';
import { LIST_ORDENES_COMPRA } from './gql.js';

const columns = [
  { key: 'fecha', label: 'Fecha' },
  { key: 'proveedor', label: 'Proveedor' },
  { key: 'estado', label: 'Estado' },
  { key: 'total', label: 'Total' },
];

export default function OrdenesList({ onCreate, onSelect, refreshKey = 0 }) {
  const [search, setSearch] = useState('');
  const [estado, setEstado] = useState('');
  const variables = useMemo(() => {
    const filters = {};
    if (estado) filters.estado = estado;
    const searchValue = search.trim() || null;
    return {
      filters,
      pagination: { limit: 50, offset: 0 },
      search: searchValue,
    };
  }, [search, estado]);
  const [{ data, fetching, error }, reexecute] = useQuery({ query: LIST_ORDENES_COMPRA, variables });

  useEffect(() => {
    reexecute({ requestPolicy: 'network-only' });
  }, [refreshKey, reexecute]);

  const rows = useMemo(() => {
    if (!data?.listOrdenesCompra?.items) {
      return [];
    }
    return data.listOrdenesCompra.items.map((item) => ({
      id: item.id,
      fecha: new Date(item.fecha).toLocaleDateString(),
      proveedor: item.proveedorNombre || `#${item.proveedorId}`,
      estado: item.estado,
      total: new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(item.total ?? 0),
    }));
  }, [data]);

  return (
    <div className="mx-auto flex w-full max-w-[1400px] flex-col gap-6 px-6 py-8">
      <PageHeader
        title="Órdenes de compra"
        subtitle="Administrá las órdenes de compra desde la solicitud hasta el cierre."
        actions={
          <button
            type="button"
            onClick={onCreate}
            className="rounded-xl bg-[rgb(var(--brand))] px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-[rgba(var(--brand-700),0.9)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
          >
            Nueva orden
          </button>
        }
      />

      <section className="rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.7)] p-6 shadow-sm">
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-12 md:col-span-6">
            <label className="mb-2 block text-sm font-medium text-[rgb(var(--fg))]" htmlFor="buscar-ordenes">
              Buscar por proveedor
            </label>
            <input
              id="buscar-ordenes"
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Ingresá razón social"
              className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
            />
          </div>
          <div className="col-span-12 md:col-span-3">
            <label className="mb-2 block text-sm font-medium text-[rgb(var(--fg))]" htmlFor="estado-ordenes">
              Estado
            </label>
            <select
              id="estado-ordenes"
              value={estado}
              onChange={(event) => setEstado(event.target.value)}
              className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
            >
              <option value="">Todos</option>
              <option value="BORRADOR">Borrador</option>
              <option value="APROBADA">Aprobada</option>
              <option value="CERRADA">Cerrada</option>
              <option value="ANULADA">Anulada</option>
            </select>
          </div>
        </div>

        <div className="mt-6 overflow-hidden rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.65)]">
          {fetching ? (
            <div className="px-4 py-6 text-sm text-muted">Cargando órdenes…</div>
          ) : error ? (
            <div className="px-4 py-6 text-sm text-red-300">{error.message}</div>
          ) : (
            <DataTable columns={columns} rows={rows} onRowClick={(row) => onSelect?.(row.id)} pageSize={10} />
          )}
        </div>
      </section>

      <StickyActions
        left={<p className="text-sm text-muted">Seleccioná una orden para ver el detalle y gestionar las recepciones.</p>}
        right={
          <button
            type="button"
            onClick={onCreate}
            className="rounded-xl border border-[rgb(var(--brand))] bg-transparent px-4 py-2 text-sm font-medium text-[rgb(var(--brand))] transition hover:bg-[rgba(var(--brand),0.1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
          >
            Crear orden
          </button>
        }
      />
    </div>
  );
}
